<template>
  <TemplateMultiStepModal
    :header="dialogs.UNDERSTAND_BETASCORE_HEADER"
    :step="step"
    :total-steps="3"
    class="calico-understand-betascore-dialog"
    @update:step="(v) => (step = v)"
    @close="$emit('close')"
  >
    <template #content="{ step }">
      <div class="content">
        <section
          v-if="step === 1"
          class="ub-section"
        >
          <h2 class="title heading--small">
            {{ dialogs.UB_STEP1_TITLE }}
          </h2>
          <p
            class="subtitle label--medium"
            v-html="intro1Html"
          />

          <ul class="stage-list">
            <li
              v-for="item in tiers"
              :key="item.key"
              class="row"
            >
              <div class="row__left">
                <div class="tier-indicator">
                  <CalicoTierIndicator
                    :active="bucketByKey[item.key]"
                    :color-class="tierColorClass(bucketByKey[item.key])"
                  />
                </div>
                <div class="row__title label--large">
                  {{ item.title }}
                </div>
              </div>
              <div class="row__desc body--medium">
                {{ item.desc }}
              </div>
            </li>
          </ul>
        </section>

        <section
          v-else-if="step === 2"
          class="ub-section"
        >
          <h2 class="title heading--small">
            {{ dialogs.UB_STEP2_TITLE }}
          </h2>
          <p
            class="subtitle label--medium"
            v-html="intro2Html"
          />

          <ul class="stage-list">
            <li
              v-for="risk in riskLevels"
              :key="risk.key"
              class="line"
            >
              <div
                class="line__title label--large"
                :class="tierColorClass(risk.key)"
              >
                {{ risk.title }}
              </div>
              <div class="line__desc body--medium">
                {{ risk.desc }}
              </div>
            </li>
          </ul>
        </section>

        <section
          v-else
          class="ub-section"
        >
          <h2 class="title heading--small">
            {{ dialogs.UB_STEP3_TITLE }}
          </h2>
          <p
            class="subtitle label--medium"
            v-html="intro3Html"
          />

          <ul class="stage-list">
            <li
              v-for="conf in confidence"
              :key="conf.key"
              class="line"
            >
              <div
                class="line__title label--large"
                :class="tierColorClass(conf.key, 'confidence')"
              >
                {{ conf.title }}
              </div>
              <div class="line__desc body--medium">
                {{ conf.desc }}
              </div>
            </li>
          </ul>
        </section>
      </div>
    </template>
  </TemplateMultiStepModal>
</template>

<script>
import TemplateMultiStepModal from '@/components/template/multiStepModal/index.vue'
import CalicoTierIndicator from '@/components/calico/tierIndicator/index.vue'
import { BetaScoreDialogs } from '@/enums'

export default {
  name: 'UnderstandBetascoreDialog',
  components: { 
    TemplateMultiStepModal, 
    CalicoTierIndicator 
  },
  data: () => ({
    step: 1,
    bucketByKey: { 
      prime: 5, 
      growth: 4, 
      emerging: 3, 
      monitor: 2, 
      caution: 1 
    },
    riskBucketByKey: { 
      low: 5, 
      medlow: 4, 
      medium: 3, 
      medhigh: 2, 
      high: 1 
    }
  }),
  computed: {
    dialogs () { return BetaScoreDialogs },
    tiers () { return BetaScoreDialogs.UB_TIERS_LIST },
    riskLevels () { return BetaScoreDialogs.UB_RISK_LIST },
    confidence () { return BetaScoreDialogs.UB_CONFIDENCE_LIST },
    intro1Html () { return this.formatIntro(this.dialogs.UB_STEP1_INTRO_TEMPLATE) },
    intro2Html () { return this.formatIntro(this.dialogs.UB_RISK_INTRO) },
    intro3Html () { return this.formatIntro(this.dialogs.UB_CONFIDENCE_INTRO) },
    tierKeyToTitle () {
      return this.tiers.reduce((acc, t) => { acc[t.key] = t.title; return acc }, {})
    },
    bucketToKey () {
      const out = {}
      Object.keys(this.bucketByKey).forEach(k => { out[this.bucketByKey[k]] = k })
      return out
    }
  },
  methods: {
    formatIntro (tpl) {
      const map = {
        tiers: this.dialogs.LABEL_TIERS,
        risk: this.dialogs.LABEL_RISK_LEVEL,
        confidence: this.dialogs.LABEL_DATA_CONFIDENCE
      }
      return String(tpl).replace(/\{(tiers|risk|confidence)\}/gi, (_, k) => `<span class="emph">${map[k.toLowerCase()]}</span>`)
    },
    getBucket (v) {
      if (typeof v === 'number') return v
      const k = String(v).toLowerCase()
      if (this.bucketByKey[k]) return this.bucketByKey[k]
      if (this.riskBucketByKey[k]) return this.riskBucketByKey[k]
      return 0
    },
    tierColorClass (v, context = '') {
      if (context === 'confidence') {
        const k = String(v).toLowerCase()
        const mapConf = { high: 'tier--prime', medium: 'tier--emerging', low: 'tier--caution' }
        return mapConf[k] || ''
      }
      const map = { 
        1: 'tier--caution', 
        2: 'tier--monitor', 
        3: 'tier--emerging', 
        4: 'tier--growth', 
        5: 'tier--prime' 
      }
      return map[this.getBucket(v)] || ''
    },
    tierTitle (v) {
      const key = typeof v === 'number' ? this.bucketToKey[v] : String(v)
      return this.tierKeyToTitle[key] || key
    }
  }
}
</script>

<style lang="scss">
.calico-understand-betascore-dialog {
  color: $color-text-primary;

  .content { 
    @include inset-space($space-small); 
  }
  .title { 
    margin-bottom: $space-xsmall; 
  }
  .subtitle { 
    margin-bottom: $space-medium; 
  }
  .emph { 
    font-weight: 700; 
    text-decoration: underline; 
  }

  .stage-list {
    @include stack-space($space-medium);
    background-color: $color-background-primary-alt;
    border-radius: $radius-medium;
    padding: $space-medium;
    gap: $space-large;
    color: $color-text-primary-alt;
  }

  .row {
    @include inline-space($space-medium);
    align-items: flex-end;

    & + .row { margin-top: $space-small; }

    .row__left {
      min-width: 100px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .tier-indicator {
      @include inline-space($space-none);
      align-items: center;
      margin-bottom: $space-small;
      height: $space-medium;
    }

    .row__desc {
      align-self: flex-end;
      flex: 1;
    }
  }

  .line { 
    @include stack-space($space-xxsmall); 
  }

  .actions { 
    display: flex; 
    justify-content: flex-end; 
  }

  .tier--prime   { color: $color-green; }
  .tier--growth  { color: $color-cyan; }
  .tier--emerging{ color: $color-yellow; }
  .tier--monitor { color: $color-orange; }
  .tier--caution { color: $color-pink; }
}
</style>
